class PartsFinder extends HTMLElement {
  constructor() {
    super();
    this.partFinderEntries = [];
    this.sectionId = null;
    this.finderMode = 'model_name_size';
    this.modelSelect = null;
    this.sizeSelect = null;
    this.modelNumberSelect = null;
    this.buttonGroup = null;
    this.resultContainer = null;
    this.handlePopState = this.handlePopState.bind(this);
  }

  connectedCallback() {
    this.initializeElements();
    this.setupEventListeners();
    this.applySelectionFromUrl();
    window.addEventListener('popstate', this.handlePopState);
  }

  disconnectedCallback() {
    window.removeEventListener('popstate', this.handlePopState);
  }

  initializeElements() {
    this.finderMode = this.getAttribute('data-finder-mode') || 'model_name_size';
    this.modelSelect = this.querySelector('#model-select');
    this.sizeSelect = this.querySelector('#size-select');
    this.modelNumberSelect = this.querySelector('#model-number-select');
    this.buttonGroup = this.querySelector('.parts-finder-buttons');
    this.resultContainer = document.querySelector('#parts-finder-result');
    
    // Get data from data attributes
    this.partFinderEntries = JSON.parse(this.getAttribute('data-entries') || '[]');
    this.sectionId = this.getAttribute('data-section-id');

    if (this.isModelNumberMode()) {
      this.sortModelNumberOptions();
    }
  }

  setupEventListeners() {
    if (this.isModelNumberMode()) {
      if (this.modelNumberSelect) {
        this.modelNumberSelect.addEventListener('change', this.updateButtonsState.bind(this));
      }
    } else if (this.modelSelect) {
      this.modelSelect.addEventListener('change', this.handleModelChange.bind(this));

      if (this.sizeSelect) {
        this.sizeSelect.addEventListener('change', this.updateButtonsState.bind(this));
      }
    }

    // Set up button event listeners
    const resetButton = this.querySelector('button[onclick="resetFinder()"]');
    const findButton = this.querySelector('button[onclick="findParts()"]');
    
    if (resetButton) {
      resetButton.removeAttribute('onclick');
      resetButton.addEventListener('click', this.resetFinder.bind(this));
    }
    
    if (findButton) {
      findButton.removeAttribute('onclick');
      findButton.addEventListener('click', this.findParts.bind(this));
    }

    this.updateButtonsState();
  }

  handleModelChange() {
    this.populateSizeOptions(this.modelSelect.value);
    this.updateButtonsState();
  }

  populateSizeOptions(selectedModel) {
    if (!this.sizeSelect) return;

    this.sizeSelect.innerHTML = '<option value="">Please Select ...</option>';
    
    if (selectedModel) {
      const matched = this.partFinderEntries.filter(entry => entry.model_name === selectedModel);
      const sizes = [...new Set(matched.map(e => e.size).filter(Boolean))];

      sizes.forEach(size => {
        this.sizeSelect.appendChild(new Option(size, size));
      });

      if (sizes.length === 1) {
        this.sizeSelect.value = sizes[0];
      }
    }
  }

  updateButtonsState() {
    if (!this.buttonGroup) return;

    if (this.isModelNumberMode()) {
      const hasCompleteSelection = Boolean(this.modelNumberSelect && this.modelNumberSelect.value);
      this.buttonGroup.classList.toggle('is-visible', hasCompleteSelection);
      return;
    }

    if (!this.modelSelect || !this.sizeSelect) return;

    const hasCompleteSelection = Boolean(this.modelSelect.value && this.sizeSelect.value);
    this.buttonGroup.classList.toggle('is-visible', hasCompleteSelection);
  }

  resetFinder() {
    if (this.isModelNumberMode()) {
      this.modelNumberSelect.value = "";
      this.updateButtonsState();
      window.location.href = this.buildPathWithParams(this.getPreservedParams());
      return;
    }

    this.modelSelect.value = "";
    this.sizeSelect.innerHTML = '<option value="">Please Select ...</option>';
    this.updateButtonsState();
    window.location.href = this.buildPathWithParams(this.getPreservedParams());
  }

  findParts() {
    if (this.isModelNumberMode()) {
      const modelNumber = this.modelNumberSelect.value;

      if (!modelNumber) {
        alert("Please select a model number.");
        return;
      }

      window.location.href = this.buildModelNumberShareUrl(modelNumber);
      return;
    }

    const model = this.modelSelect.value;
    const size = this.sizeSelect.value;
    
    if (!model || !size) {
      alert("Please select both model and size.");
      return;
    }

    window.location.href = this.buildShareUrl(model, size);
  }

  applySelectionFromUrl() {
    if (this.isModelNumberMode()) {
      if (!this.modelNumberSelect) return;

      const selection = this.getModelNumberSelectionFromUrl();

      if (!selection) return;

      this.ensureOption(this.modelNumberSelect, selection.modelNumber);
      this.modelNumberSelect.value = selection.modelNumber;
      this.updateButtonsState();
      return;
    }

    if (!this.modelSelect || !this.sizeSelect) return;

    const selection = this.getSelectionFromUrl();

    if (!selection) return;

    this.modelSelect.value = selection.model;
    this.populateSizeOptions(selection.model);
    this.sizeSelect.value = selection.size;
    this.updateButtonsState();

  }

  getSelectionFromUrl() {
    const params = new URLSearchParams(window.location.search);
    const requestedModel = params.get('model') || '';
    const requestedSize = params.get('shelter_size') || params.get('size') || '';
    const requestedFind = params.get('find') || '';

    if (requestedModel) {
      const entry = this.partFinderEntries.find(entry => {
        return entry.model_name === requestedModel && (!requestedSize || entry.size === requestedSize);
      });

      return {
        model: entry ? entry.model_name : requestedModel,
        size: entry ? entry.size : requestedSize
      };
    }

    if (!requestedFind) return null;

    const findSlug = this.slugify(requestedFind);
    const entry = this.partFinderEntries.find(entry => {
      const modelSlug = this.slugify(entry.model_name);
      const modelAndSizeSlug = this.slugify(`${entry.model_name} ${entry.size}`);
      const sizeMatches = !requestedSize || entry.size === requestedSize;

      return sizeMatches && (
        findSlug === modelSlug ||
        findSlug === modelAndSizeSlug ||
        findSlug.startsWith(`${modelSlug}-`)
      );
    });

    if (!entry) return null;

    return {
      model: entry.model_name,
      size: entry.size
    };
  }

  getModelNumberSelectionFromUrl() {
    const params = new URLSearchParams(window.location.search);
    const requestedModelNumber = params.get('model_number') || params.get('find') || '';

    if (!requestedModelNumber) return null;

    const matchedModelNumber = this.findMatchingModelNumber(requestedModelNumber);

    return {
      modelNumber: matchedModelNumber || requestedModelNumber
    };
  }

  findMatchingModelNumber(requestedModelNumber) {
    const requestedSlug = this.slugify(requestedModelNumber);

    for (const entry of this.partFinderEntries) {
      for (const modelNumber of this.getEntryModelNumbers(entry)) {
        if (this.slugify(modelNumber) === requestedSlug) {
          return String(modelNumber);
        }
      }
    }

    return null;
  }

  getEntryModelNumbers(entry) {
    const value = entry.model_number;

    if (!value) return [];
    if (Array.isArray(value)) return value;
    if (Array.isArray(value.value)) return value.value;

    if (typeof value === 'string') {
      return value.split(/[\n,•]+/).map(modelNumber => modelNumber.trim()).filter(Boolean);
    }

    return [];
  }

  handlePopState() {
    this.applySelectionFromUrl();
  }

  buildShareUrl(model, size) {
    const params = this.getPreservedParams();
    params.set('shelter_size', size);
    params.set('find', this.slugify(`${model} ${size}`));

    return this.buildPathWithParams(params);
  }

  buildModelNumberShareUrl(modelNumber) {
    const params = this.getPreservedParams();
    params.set('model_number', modelNumber);
    params.set('find', this.slugify(modelNumber));

    return this.buildPathWithParams(params);
  }

  getPreservedParams() {
    const params = new URLSearchParams(window.location.search);
    const finderParams = ['model', 'size', 'shelter_size', 'model_number', 'find'];

    finderParams.forEach(param => params.delete(param));

    if (!params.has('preview_theme_id')) {
      const theme = window.Shopify && window.Shopify.theme;

      if (theme && theme.id && theme.role && theme.role !== 'main') {
        params.set('preview_theme_id', theme.id);
      }
    }

    return params;
  }

  isModelNumberMode() {
    return this.finderMode === 'model_number';
  }

  ensureOption(select, value) {
    const optionExists = Array.from(select.options).some(option => option.value === value);

    if (!optionExists) {
      select.appendChild(new Option(value, value));
      this.sortModelNumberOptions();
    }
  }

  sortModelNumberOptions() {
    if (!this.modelNumberSelect) return;

    const selectedValue = this.modelNumberSelect.value;
    const placeholder = Array.from(this.modelNumberSelect.options).find(option => option.value === '');
    const seenValues = new Set();
    const modelNumberOptions = Array.from(this.modelNumberSelect.options)
      .filter(option => option.value)
      .map(option => option.value.trim())
      .filter(Boolean)
      .filter(value => {
        const key = value.toLowerCase();

        if (seenValues.has(key)) return false;

        seenValues.add(key);
        return true;
      })
      .sort((first, second) => {
        return first.localeCompare(second, undefined, {
          numeric: true,
          sensitivity: 'base'
        });
      });

    this.modelNumberSelect.innerHTML = '';

    if (placeholder) {
      this.modelNumberSelect.appendChild(new Option(placeholder.text, placeholder.value));
    }

    modelNumberOptions.forEach(value => {
      this.modelNumberSelect.appendChild(new Option(value, value));
    });

    this.modelNumberSelect.value = selectedValue;
  }

  buildPathWithParams(params) {
    const query = params.toString().replace(/\+/g, '%20');

    return query ? `${window.location.pathname}?${query}` : window.location.pathname;
  }

  slugify(value) {
    return String(value)
      .toLowerCase()
      .replace(/&/g, 'and')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }
}

// Register the web component
customElements.define('parts-finder', PartsFinder);
